import { useRef, useState, useEffect } from "react";
import { useStyles } from "./WorkspaceDocuments.styles";
import { FolderIcon } from "icons/FolderIcon";

export interface INewFolderRowProps {
  onCreateFolder: (name: string) => Promise<void>;
  onCancel: () => void;
}

export const NewFolderRow = ({
  onCreateFolder,
  onCancel,
}: INewFolderRowProps): JSX.Element => {
  const styles = useStyles();
  const [inputValue, setInputValue] = useState("");
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleKeyDown = async (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === "Enter") {
      await handleCreate();
    } else if (event.key === "Escape") {
      onCancel();
    }
  };

  const handleCreate = async () => {
    const trimmedName = inputValue.trim();

    if (!trimmedName) {
      setIsError(true);
      return;
    }

    setIsLoading(true);
    try {
      await onCreateFolder(trimmedName);
      onCancel();
    } catch {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    if (isError) {
      setIsError(false);
    }
  };

  return (
    <div className={styles.newFolderRow}>
      <div className={styles.newFolderIconWrapper}>
        <FolderIcon size={22} />
      </div>
      <input
        ref={inputRef}
        type="text"
        className={styles.newFolderInput}
        placeholder="Folder name"
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        disabled={isLoading}
        aria-label="Folder name"
        title={isError ? "Folder name cannot be empty" : undefined}
      />
      <button
        className={styles.newFolderCreateButton}
        onClick={handleCreate}
        disabled={isLoading || !inputValue.trim()}
        type="button"
        aria-label="Create folder"
      >
        {isLoading ? "Creating..." : "Create"}
      </button>
      <button
        className={styles.newFolderCancelButton}
        onClick={onCancel}
        disabled={isLoading}
        type="button"
        aria-label="Cancel creating folder"
      >
        Cancel
      </button>
    </div>
  );
};
